import { CustomJobPhase } from "hooks/jobs/job-phases/useGetJobPhase";
import formatCentsToUSD from "utils/money/formatCentsToUSD";

type CostItem = NonNullable<CustomJobPhase["costItems"]>[number];

export type VarianceDisplay = {
  display: string;
  color: "success" | "error" | "text";
};

export const formatJobCostingUnits = (value: number) => value.toFixed(1);

export type SummaryMetrics = {
  actualMaterialUnits: number;
  actualMaterialCost: number;
  actualLaborCost: number;
  actualEquipmentCost: number;
  actualAdditionalCost: number;
  actualSubcontractorCost: number;
  actualTruckingCost: number;
  proposedMaterialUnits: number;
  proposedMaterialCost: number;
  proposedLaborCost: number;
  proposedEquipmentCost: number;
  proposedAdditionalCost: number;
  proposedSubcontractorCost: number;
  proposedTruckingCost: number;
  actualTotalCost: number;
  proposedTotalCost: number;
  varianceMaterialUnits: VarianceDisplay;
  varianceMaterialCost: VarianceDisplay;
  varianceLaborCost: VarianceDisplay;
  varianceEquipmentCost: VarianceDisplay;
  varianceAdditionalCost: VarianceDisplay;
  varianceSubcontractorCost: VarianceDisplay;
  varianceTruckingCost: VarianceDisplay;
  varianceTotalCost: VarianceDisplay;
};

const costFieldMap = {
  materialUnits: (costItem: CostItem) => costItem?.material?.materialNeeded ?? 0,
  materialCost: (costItem: CostItem) => costItem?.costMaterial ?? 0,
  laborCost: (costItem: CostItem) => costItem?.costLabour ?? 0,
  equipmentCost: (costItem: CostItem) => costItem?.costEquipment ?? 0,
  additionalCost: (costItem: CostItem) => costItem?.costAdditional ?? 0,
  truckingCost: (costItem: CostItem) => costItem?.costTransferTruck ?? 0,
} as const;

const sumCostItems = (
  costItems: CustomJobPhase["costItems"] | undefined,
  getValue: (costItem: CostItem) => number
) => costItems?.reduce((sum, costItem) => sum + getValue(costItem), 0) ?? 0;

export const getCostItemTotal = (costItem: CostItem) =>
  (costItem?.costMaterial ?? 0) +
  (costItem?.costLabour ?? 0) +
  (costItem?.costEquipment ?? 0) +
  (costItem?.costAdditional ?? 0) +
  (costItem?.costTransferTruck ?? 0);

export const getVarianceDisplay = (
  actual: number,
  proposed: number,
  isUnits = false
): VarianceDisplay => {
  const variance = actual - proposed;
  const percent = proposed === 0 ? null : (variance / proposed) * 100;
  const color = variance < 0 ? "success" : variance > 0 ? "error" : "text";

  if (isUnits) {
    return {
      display: `${variance >= 0 ? "+" : ""}${formatJobCostingUnits(variance)}${percent === null ? " (N/A)" : ` (${percent >= 0 ? "+" : ""}${percent.toFixed(1)}%)`}`,
      color,
    };
  }

  return {
    display: `${formatCentsToUSD(variance)}${percent === null ? " (N/A)" : ` (${percent >= 0 ? "+" : ""}${percent.toFixed(1)}%)`}`,
    color,
  };
};

export const buildSummaryMetrics = (jobPhase: CustomJobPhase): SummaryMetrics => {
  const actualMaterialUnits = Number(jobPhase?.actualMaterialNeeded ?? sumCostItems(jobPhase?.costItems, costFieldMap.materialUnits));
  const actualMaterialCost = Number(jobPhase?.actualCostMaterial ?? sumCostItems(jobPhase?.costItems, costFieldMap.materialCost));
  const actualLaborCost = Number(jobPhase?.actualCostLabour ?? sumCostItems(jobPhase?.costItems, costFieldMap.laborCost));
  const actualEquipmentCost = Number(jobPhase?.actualCostEquipment ?? sumCostItems(jobPhase?.costItems, costFieldMap.equipmentCost));
  const actualAdditionalCost = Number(jobPhase?.actualCostAdditional ?? sumCostItems(jobPhase?.costItems, costFieldMap.additionalCost));
  const actualSubcontractorCost = Number(jobPhase?.actualSubcontractorCost ?? 0);
  const actualTruckingCost = Number(jobPhase?.actualCostTransferTruck ?? sumCostItems(jobPhase?.costItems, costFieldMap.truckingCost));

  const proposedMaterialUnits = Number(jobPhase?.proposedMaterialNeeded ?? 0);
  const proposedMaterialCost = Number(jobPhase?.proposedCostMaterial ?? 0);
  const proposedLaborCost = Number(jobPhase?.proposedCostLabour ?? 0);
  const proposedEquipmentCost = Number(jobPhase?.proposedCostEquipment ?? 0);
  const proposedAdditionalCost = Number(jobPhase?.proposedCostAdditional ?? 0);
  // Subcontracted proposal products store their bid cost as the phase direct cost.
  const proposedSubcontractorCost = Number(jobPhase?.proposedCostDirect ?? 0);
  const proposedTruckingCost = Number(jobPhase?.proposedCostTransferTruck ?? 0);

  const actualTotalCost = Number(
    jobPhase?.actualCostTotal ??
      actualMaterialCost +
        actualLaborCost +
        actualEquipmentCost +
        actualAdditionalCost +
        actualSubcontractorCost +
        actualTruckingCost
  );
  const proposedTotalCost =
    proposedMaterialCost +
    proposedLaborCost +
    proposedEquipmentCost +
    proposedAdditionalCost +
    proposedSubcontractorCost +
    proposedTruckingCost;

  return {
    actualMaterialUnits,
    actualMaterialCost,
    actualLaborCost,
    actualEquipmentCost,
    actualAdditionalCost,
    actualSubcontractorCost,
    actualTruckingCost,
    proposedMaterialUnits,
    proposedMaterialCost,
    proposedLaborCost,
    proposedEquipmentCost,
    proposedAdditionalCost,
    proposedSubcontractorCost,
    proposedTruckingCost,
    actualTotalCost,
    proposedTotalCost,
    varianceMaterialUnits: getVarianceDisplay(actualMaterialUnits, proposedMaterialUnits, true),
    varianceMaterialCost: getVarianceDisplay(actualMaterialCost, proposedMaterialCost),
    varianceLaborCost: getVarianceDisplay(actualLaborCost, proposedLaborCost),
    varianceEquipmentCost: getVarianceDisplay(actualEquipmentCost, proposedEquipmentCost),
    varianceAdditionalCost: getVarianceDisplay(actualAdditionalCost, proposedAdditionalCost),
    varianceSubcontractorCost: getVarianceDisplay(actualSubcontractorCost, proposedSubcontractorCost),
    varianceTruckingCost: getVarianceDisplay(actualTruckingCost, proposedTruckingCost),
    varianceTotalCost: getVarianceDisplay(actualTotalCost, proposedTotalCost),
  };
};
